import React, { useEffect, useMemo, useState } from "react";
import ReactSelect from "react-select";
import { useNavigate } from "react-router-dom";
import {
  Package, AlertCircle, Loader2, Hash, Ruler, Layers, CheckCircle2, Weight,
  ArrowRightCircle, FileText, Tag, Percent, RotateCcw,
} from "lucide-react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getProcessReceivePartyOptions,
  fetchProcessReceiveParties,
} from "../../features/processReceives/processReceivesSlice";
import {
  fetchProcessDefinitions,
  getProcessDefinitions,
} from "../../features/processDefinitions/processDefinitionsSlice";
import { PendingProcessOrderItemProps, ProcessReceiveProps } from "../../types/processReceive";
import { ProcessDefinitionProps } from "../../types/processDefinition";
import { GET, POST, PUT } from "../../services/axios.service";
import { toastStyles, filterSelectStyles } from "../../static/styles";
import { nowInIstForInput, dateToInputValue } from "../../static/datetime";
import { allowedModes, defaultMode as pickDefaultMode, localizedProcessName, formatSizeLabel, formatSize } from "../../lib/utils";
import { ProcessModePolicy } from "../../types/processDefinition";
import { route } from "../../static/route";
import { useCanAccess } from "../../hooks/useCanAccess";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { t } from "../../locales";
import { Link } from "react-router-dom";

const inputClass = (hasError: boolean) =>
  `pl-12 h-12 text-base border-2 rounded-lg transition-all duration-200 ${
    hasError
      ? "border-red-500 focus:border-red-500 focus:shadow-focus-error"
      : "border-gray-200 focus:border-[#113F67] focus:shadow-focus-brand"
  }`;

const itemInputClass = (hasError: boolean) =>
  `pl-10 h-[48px] text-base border-2 rounded-lg transition-all duration-200 ${
    hasError
      ? "border-red-500 focus:border-red-500 focus:shadow-focus-error"
      : "border-gray-200 focus:border-[#113F67] focus:shadow-focus-brand"
  }`;

const formSelectStyles: any = {
  ...filterSelectStyles,
  control: (base: any, state: any) => ({
    ...(typeof filterSelectStyles.control === "function" ? (filterSelectStyles.control as any)(base, state) : base),
    borderRadius: "0.5rem",
    fontSize: "1rem",
  }),
  singleValue: (base: any) => ({
    ...(typeof filterSelectStyles.singleValue === "function" ? (filterSelectStyles.singleValue as any)(base, {}) : base),
    fontSize: "1rem",
  }),
  placeholder: (base: any) => ({
    ...(typeof filterSelectStyles.placeholder === "function" ? (filterSelectStyles.placeholder as any)(base, {}) : base),
    fontSize: "1rem",
  }),
  input: (base: any) => ({ ...base, fontSize: "1rem" }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
};

const summaryInputClass = `pl-10 h-[48px] text-base border-2 rounded-lg transition-all duration-200 border-gray-200 focus:border-[#113F67] focus:ring-[#113F67]`;

type ReceiveItemRow = {
  checked: boolean;
  close_only: boolean;
  order_item_id: number;
  order_id: number;
  order_doc_no: string | null;
  sub_process_id: number | null;
  item_id: number;
  item_size_id: number | null;
  size_id: number | null;
  weight: number | null;
  item_name: string;
  item_size: string;
  size_name: string;
  pipe_size_name: string;
  sub_process_name: string;
  order_qty: number;
  received_qty: number;
  remaining_qty: number;
  receive_qty: number | string;
  mode: "in_house" | "outsource";
  has_rate: boolean;
  next_process_definition_id: number | null;
  next_mode: "in_house" | "outsource" | null;
  next_party_id: number | null;
  // આગળ મોકલો: empty allowed list means unrestricted.
  allowed_next_process_ids: number[];
  next_process_required: boolean;
};

type Props = {
  processDefinition: ProcessDefinitionProps;
  editReceive?: ProcessReceiveProps | null;
};

const ProcessReceiveForm = ({ processDefinition, editReceive = null }: Props) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const canAccess = useCanAccess();
  const labels = t.processReceives;
  const apiPath = "/v1/process-receives";
  const listRoute = `${route.processReceives}?process=${processDefinition.slug}`;
  const rateBasis = processDefinition.rate_basis;
  const isCasting = processDefinition.handler_key === "casting";

  const partyOptions = useAppSelector(getProcessReceivePartyOptions);
  const allDefinitions = useAppSelector(getProcessDefinitions);
  const [isLoading, setIsLoading] = useState(false);
  const baseAllowedModes = useMemo(
    () => allowedModes(processDefinition.mode_policy),
    [processDefinition.mode_policy]
  );
  // editReceive is fully loaded (not fetched async) by the time this form
  // mounts for an edit — mirrors ProcessOrderForm's defaultMode prop, except
  // a receive's mode comes back on the record itself (server-derived from
  // party_id) rather than needing a separate prop from the page.
  const [mode, setMode] = useState<"in_house" | "outsource">(
    editReceive?.mode ?? pickDefaultMode(baseAllowedModes)
  );
  const [selectedParty, setSelectedParty] = useState<{ value: number; label: string } | null>(null);
  const [partyError, setPartyError] = useState(false);
  const [selectedType, setSelectedType] = useState<"aluminium" | "white_metal">("aluminium");
  // Aluminium casting is raw-material intake, not a costed job: no rate,
  // amount, discount/tax/round-off, or payment. See
  // ProcessReceivesController::isAluminiumCasting().
  const isAluminiumCasting = isCasting && selectedType === "aluminium";
  const [voucherNo, setVoucherNo] = useState("");
  const [discountVal, setDiscountVal] = useState<number>(0);
  const [taxRateVal, setTaxRateVal] = useState<number>(0);
  const [roundOffVal, setRoundOffVal] = useState<number>(0);
  const [date, setDate] = useState(nowInIstForInput());
  const [dateError, setDateError] = useState(false);
  const [pendingItems, setPendingItems] = useState<ReceiveItemRow[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [didHydrate, setDidHydrate] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  // Per-target-process vendor party options, fetched on demand when a row
  // routes to a new process — mirrors party_process_definitions scoping.
  const [nextPartyOptionsByDef, setNextPartyOptionsByDef] = useState<Record<number, { value: number; label: string }[]>>({});

  const isEdit = !!editReceive?.id;

  // Every other active process is a valid routing target — including ones
  // with has_order===false (e.g. VMC): routing creates the downstream order
  // regardless, has_order only gates whether that process gets its own
  // manual "Add Order" screen. A row's own allowed_next_process_ids (from
  // the backend's ProcessDefinition::allowedNextProcessIds(), the definition
  // or sub-process's આગળ મોકલો whitelist) narrows this further when set —
  // an empty list means unrestricted.
  const definitionsById = useMemo(() => new Map(allDefinitions.map((d) => [d.id, d])), [allDefinitions]);
  const allActiveNextProcessOptions = useMemo(
    () => allDefinitions
      .filter((d) => d.is_active && d.id !== processDefinition.id)
      .map((d) => ({ value: d.id, label: localizedProcessName(d) })),
    [allDefinitions, processDefinition.id]
  );
  const nextProcessOptionsFor = (item: ReceiveItemRow) => {
    if (!item.allowed_next_process_ids || item.allowed_next_process_ids.length === 0) {
      return allActiveNextProcessOptions;
    }
    const allowed = new Set(item.allowed_next_process_ids);
    return allActiveNextProcessOptions.filter((o) => allowed.has(o.value));
  };

  // A routing target with no order screen of its own (VMC) is always
  // in_house regardless of its mode_policy — mirrors
  // ProcessReceivesController::validateActionableAndRouting(). Otherwise the
  // target's own mode_policy decides: a single allowed mode is forced
  // (in-house-only or outsource-only), both means the user picks.
  const targetNextModes = (targetDef: { has_order?: boolean; mode_policy?: ProcessModePolicy } | null | undefined): ("in_house" | "outsource")[] => {
    if (!targetDef) return [];
    if (targetDef.has_order === false) return ["in_house"];
    return allowedModes(targetDef.mode_policy);
  };

  useEffect(() => {
    dispatch(fetchProcessReceiveParties(processDefinition.id));
    // Guarded: an unconditional dispatch here flips getProcessDefinitionsLoading
    // back to true on every mount, which — since the parent page only renders
    // this form while that loading flag is false — unmounts/remounts it in an
    // infinite loop (each remount re-dispatching this same effect).
    if (allDefinitions.length === 0) {
      dispatch(fetchProcessDefinitions());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, processDefinition.id]);

  const fetchNextPartyOptions = async (definitionId: number) => {
    if (nextPartyOptionsByDef[definitionId]) return;
    try {
      const response: any = await GET(`/v1/process-orders-parties`, { process_definition_id: definitionId });
      if (response.data.status === "success") {
        setNextPartyOptionsByDef((prev) => ({
          ...prev,
          [definitionId]: response.data.data.map((p: any) => ({ value: p.id, label: p.firm_name || p.name })),
        }));
      }
    } catch {
      // Vendor dropdown just stays empty; the row's own validation catches an unfilled selection.
    }
  };

  // Hydrate party/date when editing, then load pending items either way
  useEffect(() => {
    if (didHydrate) return;
    if (isEdit && partyOptions.length === 0 && editReceive?.party_id) return;
    let partyOpt: { value: number; label: string } | null = null;
    let typeToUse = selectedType;
    if (isEdit) {
      partyOpt = partyOptions.find((p) => p.value === editReceive!.party_id) || null;
      setSelectedParty(partyOpt);
      if (editReceive!.date) setDate(dateToInputValue(editReceive!.date));
      if (isCasting) {
        if (editReceive!.type) {
          typeToUse = editReceive!.type as "aluminium" | "white_metal";
          setSelectedType(typeToUse);
        }
        setVoucherNo(editReceive!.voucher_no || "");
        setDiscountVal(Number(editReceive!.discount) || 0);
        setTaxRateVal(Number(editReceive!.tax_rate) || 0);
        setRoundOffVal(Number(editReceive!.round_off) || 0);
      }
    }
    loadPendingOrders(partyOpt?.value ?? null, typeToUse);
    setDidHydrate(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, partyOptions, didHydrate]);

  // Reload pending items when the casting type changes after the initial hydrate.
  useEffect(() => {
    if (!isCasting || !didHydrate) return;
    loadPendingOrders(selectedParty?.value ?? null, selectedType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType]);

  // Reload pending items when the in-house/outsource toggle changes, for
  // every process (not just casting) — this is what actually fixes the
  // "in-house order not shown because a party was forced" bug.
  useEffect(() => {
    if (!didHydrate) return;
    loadPendingOrders(selectedParty?.value ?? null, selectedType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const handleModeChange = (newMode: "in_house" | "outsource") => {
    if (newMode === mode || !baseAllowedModes.includes(newMode)) return;
    setMode(newMode);
    setPartyError(false);
    if (newMode === "in_house") {
      setSelectedParty(null);
    }
  };

  const loadPendingOrders = async (partyId: number | null, type?: string) => {
    setLoadingItems(true);
    setPendingItems([]);
    try {
      const includeReceiveId = isEdit ? editReceive!.id : undefined;
      const url = partyId
        ? `/v1/process-receives-party-orders/${partyId}`
        : `/v1/process-receives-pending-orders`;
      const params: any = { process_definition_id: processDefinition.id, mode };
      if (includeReceiveId) params.include_receive_id = includeReceiveId;
      if (isCasting) params.type = type ?? selectedType;
      const response: any = await GET(url, params);
      if (response.data.status === "success") {
        const items: PendingProcessOrderItemProps[] = response.data.data;
        const editItemMap: Record<number, {
          qty: number;
          next_process_definition_id: number | null;
          next_mode: "in_house" | "outsource" | null;
          next_party_id: number | null;
        }> = {};
        if (isEdit) {
          (editReceive!.items || []).forEach((ri) => {
            if (ri.process_order_item_id) {
              editItemMap[ri.process_order_item_id] = {
                qty: Number(ri.qty),
                next_process_definition_id: ri.next_process_definition_id ?? null,
                next_mode: ri.next_mode ?? null,
                next_party_id: ri.next_party_id ?? null,
              };
              if (ri.next_process_definition_id) fetchNextPartyOptions(ri.next_process_definition_id);
            }
          });
        }
        setPendingItems(
          items.map((item) => {
            const preset = editItemMap[item.order_item_id];
            return {
              checked: !!preset,
              close_only: false,
              order_item_id: item.order_item_id,
              order_id: item.order_id,
              order_doc_no: item.order_doc_no,
              sub_process_id: item.sub_process_id,
              item_id: item.item_id,
              item_size_id: item.item_size_id,
              size_id: item.size_id,
              weight: item.weight,
              item_name: item.item?.name || "-",
              // Kept as a raw numeric string (not label-formatted) — lineMultiplier()
              // parses this back to a number for the per-size rate calc.
              item_size: item.item_size?.size ? `${item.item_size.size}` : "-",
              // item.size is a material_sizes row ({ size, material: { name } }) — flatten
              // its nested material name before handing it to formatSizeLabel.
              size_name: formatSizeLabel(item.size ? { size: item.size.size, material_name: item.size.material?.name } : null),
              pipe_size_name: item.pipe_size?.name || "-",
              sub_process_name: item.sub_process?.name || "-",
              order_qty: item.order_qty,
              received_qty: item.received_qty,
              remaining_qty: item.remaining_qty,
              receive_qty: preset ? String(preset.qty) : "",
              mode: item.mode,
              has_rate: item.has_rate,
              next_process_definition_id: preset?.next_process_definition_id ?? null,
              next_mode: preset?.next_mode ?? null,
              next_party_id: preset?.next_party_id ?? null,
              allowed_next_process_ids: item.allowed_next_process_ids || [],
              next_process_required: !!item.next_process_required,
            };
          })
        );
      } else {
        toast.error(response.data.message, { ...(toastStyles as any) });
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t.common.somethingWentWrong, { ...(toastStyles as any) });
    }
    setLoadingItems(false);
  };

  const handlePartyChange = (opt: any) => {
    setSelectedParty(opt);
    setPartyError(false);
    setPendingItems([]);
    loadPendingOrders(opt?.value ?? null, selectedType);
  };

  const updateItem = (index: number, field: keyof ReceiveItemRow, value: any) => {
    setPendingItems((prev) => {
      const next = [...prev];
      const updated = { ...next[index], [field]: value };
      if (field === "receive_qty") {
        const qty = Number(value || 0);
        updated.close_only = qty > 0 && qty >= updated.remaining_qty;
      }
      if (field === "next_process_definition_id") {
        const targetDef = value ? definitionsById.get(value) : null;
        const targetModes = targetNextModes(targetDef);
        // A target locked to a single mode (in-house-only, outsource-only,
        // or no order screen at all) is forced — don't make the user answer
        // a one-answer question.
        updated.next_mode = targetModes.length === 1 ? targetModes[0] : null;
        updated.next_party_id = null;
      }
      if (field === "next_mode" && value !== "outsource") {
        updated.next_party_id = null;
      }
      next[index] = updated;
      return next;
    });

    if (field === "next_process_definition_id" && value) {
      fetchNextPartyOptions(value);
    }
  };

  const activeItems = pendingItems.filter(
    (item) => (item.checked && Number(item.receive_qty) > 0) || item.close_only
  );

  const getItemQtyError = (item: ReceiveItemRow): string | null => {
    const qty = Number(item.receive_qty || 0);
    if (qty > item.remaining_qty) return labels.overReceive;
    if (!attemptedSubmit) return null;
    if (qty < 0) return labels.qtyMustNotBeNegative;
    if (item.checked && !item.close_only && (item.receive_qty === "" || qty <= 0)) {
      return labels.qtyRequired;
    }
    return null;
  };

  // Mirrors ProcessReceivesController::validateActionableAndRouting() — a
  // target locked to a single mode needs no further input once selected.
  const getItemRoutingError = (item: ReceiveItemRow): string | null => {
    if (!item.next_process_definition_id) {
      if (attemptedSubmit && item.next_process_required && Number(item.receive_qty) > 0) {
        return labels.nextProcessRequired;
      }
      return null;
    }
    if (!attemptedSubmit) return null;
    const targetDef = definitionsById.get(item.next_process_definition_id);
    if (targetNextModes(targetDef).length === 1) return null;
    if (!item.next_mode) return labels.nextModeRequired;
    if (item.next_mode === "outsource" && !item.next_party_id) return labels.nextPartyRequired;
    return null;
  };

  const positiveOrOne = (n: number | null | undefined) => (Number.isFinite(n) && Number(n) > 0 ? Number(n) : 1);

  const lineMultiplier = (item: ReceiveItemRow): number => {
    if (rateBasis === "weight") return item.weight ?? 0;
    if (rateBasis === "size") return positiveOrOne(Number(item.item_size));
    return 1;
  };

  // Resolves what actually gets sent for a row's routing, given its qty —
  // shared by the submit-blocking check and the payload builder so they can
  // never disagree (mirrors the qty>0 gating in ProcessReceivesController::store()).
  const resolveNextRouting = (item: ReceiveItemRow, qty: number) => {
    const nextDefId = qty > 0 ? item.next_process_definition_id : null;
    if (!nextDefId) return { next_process_definition_id: null, next_mode: null, next_party_id: null };
    const targetDef = definitionsById.get(nextDefId);
    const targetModes = targetNextModes(targetDef);
    const nextMode = targetModes.length === 1 ? targetModes[0] : item.next_mode;
    const nextPartyId = nextMode === "outsource" ? item.next_party_id : null;
    return { next_process_definition_id: nextDefId, next_mode: nextMode, next_party_id: nextPartyId };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");
    setAttemptedSubmit(true);

    if (mode === "outsource" && !selectedParty) {
      setPartyError(true);
      return;
    }

    if (!date) {
      setDateError(true);
      return;
    }

    const interactedItems = pendingItems.filter((item) => item.checked || item.close_only);
    if (interactedItems.length === 0) {
      setSubmitError(labels.selectAtLeastOneItem);
      return;
    }

    const hasBlockingErrors = interactedItems.some((item) => {
      const qty = Number(item.receive_qty || 0);
      if (qty < 0) return true;
      if (item.checked && !item.close_only && (item.receive_qty === "" || qty <= 0)) return true;
      if (qty > 0 && item.checked && !item.has_rate) return true;
      if (qty > 0 && item.next_process_required && !item.next_process_definition_id) return true;
      if (qty > 0 && item.next_process_definition_id) {
        const routing = resolveNextRouting(item, qty);
        if (!routing.next_mode) return true;
        if (routing.next_mode === "outsource" && !routing.next_party_id) return true;
      }
      return false;
    });
    if (hasBlockingErrors) return;

    setIsLoading(true);

    try {
      const payload: any = {
        process_definition_id: processDefinition.id,
        mode,
        party_id: mode === "outsource" ? (selectedParty?.value || null) : null,
        date,
        items: activeItems.map((item) => {
          const qty = item.checked ? Number(item.receive_qty || 0) : 0;
          const isClosed = item.close_only || (qty > 0 && qty >= item.remaining_qty);
          const routing = resolveNextRouting(item, qty);
          return {
            order_item_id: item.order_item_id,
            order_id: item.order_id,
            sub_process_id: item.sub_process_id,
            item_id: item.item_id,
            item_size_id: item.item_size_id,
            size_id: item.size_id,
            qty,
            is_closed: isClosed,
            ...routing,
          };
        }),
      };
      if (isCasting) {
        payload.type = selectedType;
        if (!isAluminiumCasting) {
          payload.voucher_no = voucherNo;
          payload.discount = Number(discountVal) || 0;
          payload.tax_rate = Number(taxRateVal) || 0;
          payload.round_off = Number(roundOffVal) || 0;
        }
      }

      const response: any = isEdit
        ? await PUT(`${apiPath}/${editReceive!.id}`, payload)
        : await POST(apiPath, payload);
      if (response.data.status === "success") {
        toast.success(response.data.message, { ...(toastStyles as any) });
        navigate(listRoute);
      } else {
        toast.error(response.data.message, { ...(toastStyles as any) });
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || t.common.somethingWentWrong,
        { ...(toastStyles as any) }
      );
    }
    setIsLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
        <CardContent className="pt-6 space-y-6">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="space-y-2 w-full md:w-[30%]">
              <Label className="text-sm font-semibold text-[#113F67]">{labels.modeLabel}</Label>
              <div className="flex rounded-lg border-2 border-gray-200 overflow-hidden h-12">
                <button
                  type="button"
                  disabled={!baseAllowedModes.includes("in_house")}
                  className={`flex-1 text-sm font-medium transition-colors ${mode === "in_house" ? "bg-[#113F67] text-white" : "bg-white text-[#113F67]"} ${!baseAllowedModes.includes("in_house") ? "opacity-40 cursor-not-allowed" : ""}`}
                  onClick={() => handleModeChange("in_house")}
                >
                  {labels.inHouseLabel}
                </button>
                <button
                  type="button"
                  disabled={!baseAllowedModes.includes("outsource")}
                  className={`flex-1 text-sm font-medium transition-colors ${mode === "outsource" ? "bg-[#113F67] text-white" : "bg-white text-[#113F67]"} ${!baseAllowedModes.includes("outsource") ? "opacity-40 cursor-not-allowed" : ""}`}
                  onClick={() => handleModeChange("outsource")}
                >
                  {labels.outsourceLabel}
                </button>
              </div>
              {!baseAllowedModes.includes("outsource") ? (
                <p className="text-xs text-gray-500">{labels.modeLockedHint}</p>
              ) : !baseAllowedModes.includes("in_house") ? (
                <p className="text-xs text-gray-500">{labels.modeLockedOutsourceHint}</p>
              ) : null}
            </div>

            {mode === "outsource" && (
              <div className="space-y-2 w-full md:w-[40%]">
                <Label className="text-sm font-semibold text-[#113F67]">
                  {labels.partyLabel} <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Package className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                  <ReactSelect
                    styles={formSelectStyles}
                    options={partyOptions}
                    value={selectedParty}
                    onChange={handlePartyChange}
                    placeholder={labels.selectParty}
                    isClearable
                  />
                </div>
                {partyError ? (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />{labels.partyRequired}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500">{labels.selectPartyToView}</p>
                )}
              </div>
            )}

            <div className={mode === "outsource" ? "space-y-2 w-full md:w-[30%]" : "space-y-2 w-full md:w-[70%]"}>
              <Label className="text-sm font-semibold text-[#113F67]">
                {labels.dateLabel} <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  type="datetime-local"
                  className={`${inputClass(dateError)} [&::-webkit-calendar-picker-indicator]:hidden`}
                  style={{ paddingLeft: "1rem" }}
                  value={date}
                  onChange={(e) => { setDate(e.target.value); setDateError(false); }}
                  onClick={(e) => (e.target as HTMLInputElement).showPicker?.()}
                />
              </div>
              {dateError && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />{labels.dateRequired}
                </p>
              )}
            </div>
          </div>

          {isCasting && (
            <div className="flex flex-col md:flex-row gap-6">
              <div className={isAluminiumCasting ? "space-y-2 w-full" : "space-y-2 w-full md:w-1/2"}>
                <Label className="text-sm font-semibold text-[#113F67]">
                  {labels.typeLabel} <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Package className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                  <ReactSelect
                    styles={formSelectStyles}
                    options={[
                      { value: "aluminium", label: labels.typeAluminium },
                      { value: "white_metal", label: labels.typeWhiteMetal },
                    ]}
                    value={{
                      value: selectedType,
                      label: selectedType === "aluminium" ? labels.typeAluminium : labels.typeWhiteMetal,
                    }}
                    onChange={(opt: any) => setSelectedType(opt?.value || "aluminium")}
                    placeholder={labels.selectType}
                    isClearable={false}
                  />
                </div>
              </div>

              {!isAluminiumCasting && (
              <div className="space-y-2 w-full md:w-1/2">
                <Label className="text-sm font-semibold text-[#113F67]">{labels.voucherLabel}</Label>
                <div className="relative">
                  <FileText className="absolute left-4 top-4 h-5 w-5 text-[#34699A]" />
                  <Input
                    className={inputClass(false)}
                    placeholder={labels.voucherPlaceholder}
                    value={voucherNo}
                    onChange={(e) => setVoucherNo(e.target.value)}
                  />
                </div>
              </div>
              )}
            </div>
          )}

          <div className="border-t-2 border-[#113F67]/10" />

          <div>
            <div className="mb-4">
              <Label className="text-lg font-bold text-[#113F67]">{labels.pendingOrderItemsLabel}</Label>
              <p className="text-sm text-gray-500 mt-1">{labels.pendingItemsHelp}</p>
            </div>

            {loadingItems ? (
              <div className="flex items-center justify-center gap-2 text-gray-500 py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#113F67]" />
                {labels.loadingItems}
              </div>
            ) : pendingItems.length === 0 ? (
              <p className="text-center text-gray-500 py-8">{labels.noPendingItems}</p>
            ) : (
              <div className="space-y-3">
                {pendingItems.map((item, index) => {
                  const qtyError = getItemQtyError(item);
                  const rateBlocked = item.mode === "outsource" && !item.has_rate;
                  const nextTargetModes = item.next_process_definition_id
                    ? targetNextModes(definitionsById.get(item.next_process_definition_id))
                    : [];
                  return (
                    <div
                      key={item.order_item_id}
                      className={`group rounded-xl overflow-hidden transition-all duration-200 ${
                        rateBlocked ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
                      } ${
                        item.checked
                          ? "bg-gradient-to-br from-blue-50 to-white border-2 border-[#113F67] shadow-md"
                          : "bg-white border-2 border-gray-200 hover:border-[#34699A] hover:shadow-md"
                      }`}
                      onClick={() => {
                        if (!item.checked && !rateBlocked) updateItem(index, "checked", true);
                      }}
                    >
                      <div className="p-4 md:p-5">
                        <div className="flex items-start gap-3">
                          <label className="relative flex-shrink-0 mt-0.5" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              className="peer sr-only"
                              checked={item.checked}
                              disabled={rateBlocked}
                              onChange={(e) => updateItem(index, "checked", e.target.checked)}
                            />
                            <span className="flex items-center justify-center w-6 h-6 rounded-md border-2 border-gray-300 bg-white peer-checked:bg-[#113F67] peer-checked:border-[#113F67] transition-all">
                              {item.checked && <CheckCircle2 className="h-4 w-4 text-white" strokeWidth={3} />}
                            </span>
                          </label>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-2 rounded-full bg-[#113F67] text-white text-xs font-bold flex-shrink-0">
                                {index + 1}
                              </span>
                              <h3 className="text-base md:text-lg font-bold text-[#113F67] truncate">
                                {isAluminiumCasting ? item.size_name : item.item_name}
                              </h3>
                              <span className="inline-flex items-center text-[11px] font-semibold text-[#113F67] bg-[#113F67]/10 px-2 py-0.5 rounded-full">
                                {labels.orderLabel.replace("{n}", item.order_doc_no || String(item.order_id))}
                              </span>
                              <Badge className="text-[11px]" variant={item.mode === "outsource" ? "default" : "secondary"}>
                                {item.mode === "outsource" ? labels.outsourceLabel : labels.inHouseLabel}
                              </Badge>
                            </div>

                            {item.mode === "outsource" && !item.has_rate && (
                              <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                                <AlertCircle className="h-3 w-3 flex-shrink-0" />
                                {labels.rateNotSetForParty}
                                {selectedParty && (
                                  <>
                                    {" "}
                                    <Link to={route.partiesRates.replace(":id", String(selectedParty.value))} className="underline" onClick={(e) => e.stopPropagation()}>
                                      {labels.setRateLink}
                                    </Link>
                                  </>
                                )}
                              </p>
                            )}

                            <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                              {/* Aluminium's raw material (with its size) is already the row title above — no need to repeat it here. */}
                              {isCasting && !isAluminiumCasting && (
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-700 bg-gray-100 px-2 py-1 rounded-md">
                                  <Ruler className="h-3 w-3 text-[#34699A]" />
                                  {labels.sizeLabel}: <strong className="text-[#113F67]">{item.size_name}</strong>
                                </span>
                              )}
                              {!isCasting && item.item_size !== "-" && (
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-700 bg-gray-100 px-2 py-1 rounded-md">
                                  <Ruler className="h-3 w-3 text-[#34699A]" />
                                  {labels.itemSizeLabel}: <strong className="text-[#113F67]">{formatSize(item.item_size)}</strong>
                                </span>
                              )}
                              {!isCasting && item.sub_process_name !== "-" && (
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-700 bg-gray-100 px-2 py-1 rounded-md">
                                  <Layers className="h-3 w-3 text-[#34699A]" />
                                  {labels.subProcessLabel}: <strong className="text-[#113F67]">{item.sub_process_name}</strong>
                                </span>
                              )}
                              {rateBasis === "weight" && !isAluminiumCasting && item.weight !== null && (
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-700 bg-gray-100 px-2 py-1 rounded-md">
                                  <Weight className="h-3 w-3 text-[#34699A]" />
                                  {labels.weightLabel}: <strong className="text-[#113F67]">{item.weight}</strong>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="mt-3 grid grid-cols-3 gap-2">
                          <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2">
                            <div className="text-[10px] font-semibold tracking-wide text-gray-500 uppercase">{labels.orderQtyLabel}</div>
                            <div className="text-sm md:text-base font-bold text-gray-800">{item.order_qty}</div>
                          </div>
                          <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2">
                            <div className="text-[10px] font-semibold tracking-wide text-gray-500 uppercase">{labels.receivedQtyLabel}</div>
                            <div className="text-sm md:text-base font-bold text-gray-800">{item.received_qty}</div>
                          </div>
                          <div className="rounded-lg bg-[#113F67]/5 border border-[#113F67]/30 px-3 py-2">
                            <div className="text-[10px] font-semibold tracking-wide text-[#113F67] uppercase">{labels.remainingQtyLabel}</div>
                            <div className="text-sm md:text-base font-bold text-[#113F67]">{item.remaining_qty}</div>
                          </div>
                        </div>
                      </div>

                      {item.checked && (
                        <div className="px-4 md:px-5 pb-4 md:pb-5 pt-3 border-t border-[#113F67]/15 bg-white/60">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                              <Label className="text-xs font-semibold text-[#113F67]">
                                {labels.receiveQtyLabel} <span className="text-red-500">*</span>
                              </Label>
                              <div className="relative">
                                <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#34699A]" />
                                <Input
                                  type="number"
                                  min={0}
                                  className={itemInputClass(!!qtyError)}
                                  placeholder="0"
                                  value={item.receive_qty}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => updateItem(index, "receive_qty", e.target.value)}
                                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                />
                              </div>
                              {qtyError && (
                                <p className="text-xs text-red-500 flex items-center gap-1">
                                  <AlertCircle className="h-3 w-3" />{qtyError}
                                </p>
                              )}
                            </div>

                            <div className="space-y-1.5" onClick={(e) => e.stopPropagation()}>
                              <Label className="text-xs font-semibold text-[#113F67]">
                                {labels.nextProcessLabel}{item.next_process_required && <span className="text-red-500"> *</span>}
                              </Label>
                              <div className="relative">
                                <ArrowRightCircle className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#34699A] z-10 pointer-events-none" />
                                <ReactSelect
                                  menuPortalTarget={document.body}
                                  styles={formSelectStyles}
                                  options={nextProcessOptionsFor(item)}
                                  value={nextProcessOptionsFor(item).find((o) => o.value === item.next_process_definition_id) || null}
                                  onChange={(opt: any) => updateItem(index, "next_process_definition_id", opt?.value || null)}
                                  placeholder={labels.nextProcessPlaceholder}
                                  isClearable
                                />
                              </div>
                            </div>

                            {item.next_process_definition_id && nextTargetModes.length > 1 && (
                              <div className="space-y-1.5" onClick={(e) => e.stopPropagation()}>
                                <Label className="text-xs font-semibold text-[#113F67]">
                                  {labels.nextModeLabel} <span className="text-red-500">*</span>
                                </Label>
                                <ReactSelect
                                  menuPortalTarget={document.body}
                                  styles={formSelectStyles}
                                  options={[
                                    { value: "in_house", label: labels.inHouseLabel },
                                    { value: "outsource", label: labels.outsourceLabel },
                                  ]}
                                  value={item.next_mode ? { value: item.next_mode, label: item.next_mode === "outsource" ? labels.outsourceLabel : labels.inHouseLabel } : null}
                                  onChange={(opt: any) => updateItem(index, "next_mode", opt?.value || null)}
                                  placeholder={labels.nextModePlaceholder}
                                  isClearable
                                />
                              </div>
                            )}

                            {item.next_process_definition_id && item.next_mode === "outsource" && (
                              <div className="space-y-1.5" onClick={(e) => e.stopPropagation()}>
                                <Label className="text-xs font-semibold text-[#113F67]">
                                  {labels.nextPartyLabel} <span className="text-red-500">*</span>
                                </Label>
                                <ReactSelect
                                  menuPortalTarget={document.body}
                                  styles={formSelectStyles}
                                  options={nextPartyOptionsByDef[item.next_process_definition_id] || []}
                                  value={(nextPartyOptionsByDef[item.next_process_definition_id] || []).find((o) => o.value === item.next_party_id) || null}
                                  onChange={(opt: any) => updateItem(index, "next_party_id", opt?.value || null)}
                                  placeholder={labels.nextPartyPlaceholder}
                                  isClearable
                                />
                              </div>
                            )}
                          </div>
                          {(() => {
                            const routingError = getItemRoutingError(item);
                            return routingError ? (
                              <p className="mt-2 text-xs text-red-500 flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" />{routingError}
                              </p>
                            ) : null;
                          })()}
                          {rateBasis !== "piece" && !isAluminiumCasting && Number(item.receive_qty) > 0 && (() => {
                            const qtyN = Number(item.receive_qty);
                            const multiplier = lineMultiplier(item);
                            const total = qtyN * multiplier;
                            return (
                              <div className="mt-3 rounded-lg bg-[#113F67]/5 border border-[#113F67]/20 px-3 py-2 text-xs md:text-sm text-[#113F67] font-medium" onClick={(e) => e.stopPropagation()}>
                                {labels.calculationPrefix} <span className="font-bold">{qtyN}</span> × <span className="font-bold">{multiplier}</span> = <span className="font-bold">{total}</span>
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      <div
                        className="px-4 md:px-5 pb-4 md:pb-5 pt-3 border-t border-dashed border-gray-200 bg-white/40"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            className="w-4 h-4 rounded border-gray-300 accent-[#113F67]"
                            checked={item.close_only}
                            onChange={(e) => updateItem(index, "close_only", e.target.checked)}
                          />
                          <span className="text-xs font-semibold text-gray-700">{labels.closeItemLabel}</span>
                          <span className="text-[11px] text-gray-500">{labels.closeItemHint}</span>
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {isCasting && !isAluminiumCasting && activeItems.length > 0 && canAccess("money-view") && (
              <div className="mt-6 pt-4 border-t-2 border-[#113F67]/10">
                <div className="md:flex md:justify-end">
                  <div className="w-full md:max-w-md space-y-3">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1 md:gap-4">
                      <Label className="text-sm font-semibold text-gray-600 whitespace-nowrap">
                        {labels.discountLabel}
                      </Label>
                      <div className="relative w-full md:max-w-[220px]">
                        <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#34699A]" />
                        <Input
                          type="number"
                          step="0.01"
                          className={summaryInputClass + " text-right w-full"}
                          value={discountVal}
                          onChange={(e) => setDiscountVal(Number(e.target.value) || 0)}
                          onWheel={(e) => (e.target as HTMLInputElement).blur()}
                        />
                      </div>
                    </div>

                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1 md:gap-4">
                      <Label className="text-sm font-semibold text-gray-600 whitespace-nowrap">
                        {labels.taxRateLabel}
                      </Label>
                      <div className="relative w-full md:max-w-[220px]">
                        <Percent className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#34699A]" />
                        <Input
                          type="number"
                          step="0.01"
                          className={summaryInputClass + " text-right w-full"}
                          value={taxRateVal}
                          onChange={(e) => setTaxRateVal(Number(e.target.value) || 0)}
                          onWheel={(e) => (e.target as HTMLInputElement).blur()}
                        />
                      </div>
                    </div>

                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1 md:gap-4">
                      <Label className="text-sm font-semibold text-gray-600 whitespace-nowrap">
                        {labels.roundOffLabel}
                      </Label>
                      <div className="relative w-full md:max-w-[220px]">
                        <RotateCcw className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#34699A]" />
                        <Input
                          type="number"
                          step="0.01"
                          className={summaryInputClass + " text-right w-full"}
                          value={roundOffVal}
                          onChange={(e) => setRoundOffVal(Number(e.target.value) || 0)}
                          onWheel={(e) => (e.target as HTMLInputElement).blur()}
                        />
                      </div>
                    </div>

                    <p className="text-xs text-gray-500 pt-2 border-t border-[#113F67]/10">
                      {labels.totalComputedOnSaveHint}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {submitError && (
            <p className="text-sm text-red-500 flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />{submitError}
            </p>
          )}

          <div className="border-t-2 border-[#113F67]/10" />

          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              className="h-[48px] px-6 rounded-lg border-2 border-[#34699A] text-[#113F67] hover:bg-[#113F67]/5 bg-white text-base"
              onClick={() => navigate(listRoute)}
              disabled={isLoading}
            >
              {labels.cancelButton}
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="h-[48px] px-6 rounded-lg border-2 border-[#113F67] bg-gradient-to-r from-[#113F67] to-[#34699A] hover:from-[#0d2f4d] hover:to-[#2a547b] text-base"
            >
              {isLoading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{labels.saving}</>
              ) : (
                isEdit ? labels.updateReceiveButton : labels.saveReceiveButton
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  );
};

export default ProcessReceiveForm;
